<template>
    <div class="w3-container">
        <edit-domain ref="edit-domain" @created="finishAddDomain($event)" @updated="finishEditDomain($event)" @deleted="finishDeleteDomain($event)" />

        <p>{{ $t('Manage domains for use with the dynamic DNS service.') }}</p>

        <p>
            <button class="twi-button w3-button w3-small" @click="beginAddDomain()">{{ $t('Add domain') }}</button>
        </p>

        <div v-show="domainsLoading" class="w3-center">
            <img src="@/assets/img/spinner-green-48.gif" :alt="$t('Loading')"><br>
            <strong>{{ $t('Loading domains ...') }}</strong>
        </div>
        <div v-show="!domainsLoading">
            <table class="w3-table twi-table">
                <thead>
                    <tr>
                        <th>
                            <a href="javascript:void(0)" @click="setSortField('domain')">{{ $t('Domain') }}</a>
                            <img v-show="sortField === 'domain' && sortOrder === 'ASC'" src="@/assets/img/sort-asc-white-16.png" alt="ASC">
                            <img v-show="sortField === 'domain' && sortOrder === 'DESC'" src="@/assets/img/sort-desc-white-16.png" alt="DESC">
                        </th>
                        <th>
                            <a href="javascript:void(0)" @click="setSortField('token')">{{ $t('Token') }}</a>
                            <img v-show="sortField === 'token' && sortOrder === 'ASC'" src="@/assets/img/sort-asc-white-16.png" alt="ASC">
                            <img v-show="sortField === 'token' && sortOrder === 'DESC'" src="@/assets/img/sort-desc-white-16.png" alt="DESC">
                        </th>
                        <th>
                            <a href="javascript:void(0)" @click="setSortField('created')">{{ $t('Created') }}</a>
                            <img v-show="sortField === 'created' && sortOrder === 'ASC'" src="@/assets/img/sort-asc-white-16.png" alt="ASC">
                            <img v-show="sortField === 'created' && sortOrder === 'DESC'" src="@/assets/img/sort-desc-white-16.png" alt="DESC">
                        </th>
                        <th>
                            <a href="javascript:void(0)" @click="setSortField('modified')">{{ $t('Modified') }}</a>
                            <img v-show="sortField === 'modified' && sortOrder === 'ASC'" src="@/assets/img/sort-asc-white-16.png" alt="ASC">
                            <img v-show="sortField === 'modified' && sortOrder === 'DESC'" src="@/assets/img/sort-desc-white-16.png" alt="DESC">
                        </th>
                        <th>
                            <a href="javascript:void(0)" @click="setSortField('active')">{{ $t('Active') }}</a>
                            <img v-show="sortField === 'active' && sortOrder === 'ASC'" src="@/assets/img/sort-asc-white-16.png" alt="ASC">
                            <img v-show="sortField === 'active' && sortOrder === 'DESC'" src="@/assets/img/sort-desc-white-16.png" alt="DESC">
                        </th>
                        <th>
                            <a href="javascript:void(0)" @click="setSortField('dnsLastUpdate')">{{ $t('Last DNS update') }}</a>
                            <img v-show="sortField === 'dnsLastUpdate' && sortOrder === 'ASC'" src="@/assets/img/sort-asc-white-16.png" alt="ASC">
                            <img v-show="sortField === 'dnsLastUpdate' && sortOrder === 'DESC'" src="@/assets/img/sort-desc-white-16.png" alt="DESC">
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-show="domains.length === 0">
                        <td colspan="6" class="w3-center"><strong>{{ $t('No mapped domains found.') }}</strong></td>
                    </tr>

                    <tr v-for="domain in domains" :key="domain.domain_id" @click="beginEditDomain(domain)">
                        <td>{{ domain.getFqdn() }}</td>
                        <td>{{ domain.token }}</td>
                        <td>{{ $datetime.format(domain.created, null, $datetime.getDateFormat()) }}</td>
                        <td>{{ domain.modified === null ? $t('N/A') : $datetime.format(domain.modified, null, $datetime.getDateFormat()) }}</td>
                        <td>{{ domain.active === true ? $t('Yes') : $t('No') }}</td>
                        <td>{{ formatLastDnsUpdate(domain.dnsLastUpdate) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import '@/utils/DateTime'

import Api from 'Api'

import Domain from '@/model/Domain'

import EditDomain from '@/components/EditDomain'

export default {
    name: 'Domains',

    components: {
        EditDomain
    },

    data () {
        return {
            domainsLoading: false,
            domains: [],
            sortField: 'domain',
            sortOrder: 'DESC'
        }
    },

    mounted () {
        this.loadDomains()
    },

    methods: {
        loadDomains () {
            let self = this

            self.domainsLoading = true
            Api.getDomains(null, `${self.sortField} ${self.sortOrder}`)
                .then(resp => {
                    if (typeof resp.data !== 'object') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    let domains = []
                    try {
                        resp.data.forEach(it => domains.push(Domain.fromObject(it)))
                    } catch (_err) {
                        console.debug(_err)
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }
                    self.domains = domains
                })
                .catch(err => {
                    if (typeof err.response === 'undefined' || typeof err.response.status !== 'number') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    let text = 'Invalid API response.'
                    switch (err.response.status) {
                    case 403:
                        text = 'You are not authorized to access domains.'
                        break
                    
                    case 400:
                        let message = typeof err.response.data.message === 'string'
                            ? `:<br><tt>${err.response.data.message}</tt>`
                            : '.'
                        text = `Invalid request${message}`
                        break
                    default: // pass
                    }

                    window.toast({
                        text: self.$i18n.t(text),
                        color: 'red'
                    })
                })
                .finally(() => self.domainsLoading = false)
        },

        setSortField (field) {
            if (this.sortField === field) {
                this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC'
            } else {
                this.sortField = field
                this.sortOrder = 'ASC'
            }
            this.sortDomains()
        },

        sortDomains () {
            const self = this
            if (self.domains.length < 2) {
                return
            }
            if (typeof self.domains[0][self.sortField] === 'undefined') {
                return
            }

            self.domains.sort((a, b) => {
                let sorta = null
                let sortb = null
                switch (self.sortField) {
                case 'domain':
                    sorta = a.getFqdn()
                    sortb = b.getFqdn()
                    break
                default:
                    sorta = a[self.sortField]
                    sortb = b[self.sortField]
                }

                if (sorta === sortb) {
                    return 0
                }
                if (self.sortOrder === 'ASC') {
                    return sorta < sortb ? -1 : 1
                } else {
                    return sorta > sortb ? -1 : 1
                }
            })
        },

        formatLastDnsUpdate (v) {
            return v === null ? self.$i18n.t('N/A') : this.$datetime.format(v, null, this.$datetime.getDateTimeFormat())
        },

        beginAddDomain () {
            this.$refs['edit-domain'].showCreate()
        },

        finishAddDomain (event) {
            if (event.data instanceof Domain) {
                this.domains.push(event.data)
                this.sortDomains()
            }
        },

        beginEditDomain (domain) {
            if (typeof domain === 'undefined' || !(domain instanceof Object)) {
                return
            }

            this.$refs['edit-domain'].showEdit(domain)
        },

        finishEditDomain (event) {
            if (event.data instanceof Domain) {
                this.domains = this.domains.filter(it => it.recordId !== event.data.recordId)
                this.domains.push(event.data)
                this.sortDomains()
            }
        },

        finishDeleteDomain (event) {
            if (event.data instanceof Domain) {
                this.domains = this.domains.filter(it => it.recordId !== event.data.recordId)
            }
        }
    }
}
</script>

<i18n src="@/lang/common.json"></i18n>
<i18n src="@/lang/domains.json"></i18n>
